import { SkeletalPosition } from "mannequin/src/types/tracking/raw";
import {
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Vector3,
} from "three";

const skeletalStructure: { [key: string]: string[] } = {
  Neck: [
    "Nose",
    "REye",
    "REar",
    "LEye",
    "LEar",
    "RShoulder",
    "LShoulder",
    "_spineLower",
  ],
  RShoulder: ["RHip", "RElbow"],
  RElbow: ["RWrist"],
  LShoulder: ["LHip", "LElbow"],
  LElbow: ["LWrist"],
  RHip: ["_waist", "RKnee"],
  LHip: ["LKnee"],
  RKnee: ["RAnkle"],
  LKnee: ["LAnkle"],
};

export class TrackingSkeleton extends LineSegments {
  structure: { [key: string]: string[] };

  constructor() {
    const geometry = new BufferGeometry();

    const vertices: number[] = [];
    const vColors: number[] = [];

    for (const parent in skeletalStructure) {
      const joint = skeletalStructure[parent];

      for (const _ of joint) {
        const color1 = new Color(0xff0000);
        const color2 = new Color(0x00ff00);
        vertices.push(0, 0, 0);
        vertices.push(0, 0, 0);
        vColors.push(color1.r, color1.g, color1.b);
        vColors.push(color2.r, color2.g, color2.b);
      }
    }

    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));
    geometry.setAttribute("color", new Float32BufferAttribute(vColors, 3));

    const material = new LineBasicMaterial({
      vertexColors: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      transparent: true,
      linewidth: 5,
    });

    super(geometry, material);

    //this.type = "PointSkeleton";
    this.matrix = new Matrix4();
    this.matrixAutoUpdate = false;

    this.frustumCulled = false;

    this.structure = skeletalStructure;
  }

  update(points: JointPositions) {
    const geometry = this.geometry;
    const position = geometry.getAttribute("position");

    let offset = 0;
    for (const parent in this.structure) {
      const joint = this.structure[parent];

      for (const child of joint) {
        // TODO: add smoothing / filtering here
        // TODO: check that previous value is retained
        const parentTarget = points.get(parent);
        const childTarget = points.get(child);
        if (parentTarget) {
          position.setXYZ(
            offset,
            parentTarget.x,
            parentTarget.y,
            parentTarget.z
          );
        }
        if (childTarget) {
          position.setXYZ(
            offset + 1,
            childTarget.x,
            childTarget.y,
            childTarget.z
          );
        }
        offset += 2;
      }
    }
    this.updateMatrixWorld(true);
  }

  updateMatrixWorld(force: boolean) {
    this.geometry.getAttribute("position").needsUpdate = true;
    super.updateMatrixWorld(force);
  }
}

const jointNames: { [key: number]: string } = {
  0: "Nose",
  1: "Neck",
  2: "RShoulder",
  3: "RElbow",
  4: "RWrist",
  5: "LShoulder",
  6: "LElbow",
  7: "LWrist",
  8: "MidHip",
  9: "RHip",
  10: "RKnee",
  11: "RAnkle",
  12: "LHip",
  13: "LKnee",
  14: "LAnkle",
  15: "REye",
  16: "LEye",
  17: "REar",
  18: "LEar",
};

export class JointPositions {
  actorId: number;
  positionId: number;

  namedPoints: { [key: string]: Vector3 } = {};

  private jointCnt = 0;

  constructor(positionId?: number, actorId = -1) {
    this.actorId = actorId;
    this.positionId = positionId ?? 0;
  }

  get(name: string): Vector3 | undefined {
    return this.namedPoints[name];
  }

  private set(name: string, v: Vector3): void {
    if (this.namedPoints[name] === undefined) {
      this.jointCnt++;
    }
    this.namedPoints[name] = v;
  }

  getCentroid(centroid?: Vector3): Vector3 {
    if (!centroid) {
      centroid = new Vector3();
    } else {
      centroid.set(0, 0, 0);
    }
    for (const name in this.namedPoints) {
      const point = this.namedPoints[name];
      centroid.add(point);
    }

    centroid.divideScalar(this.jointCnt);
    return centroid;
  }

  updateFromSkeletalPosition(position: SkeletalPosition): JointPositions {
    this.namedPoints = {};

    for (const joint of position.joints) {
      const name = jointNames[joint.id];
      if (name) this.set(name, new Vector3(joint.x, joint.z, -joint.y));
    }

    // make waist point
    const lhip = this.get("LHip");
    const rhip = this.get("RHip");
    if (lhip && rhip) {
      const waist = new Vector3();
      waist.lerpVectors(lhip, rhip, 0.5);
      this.set("_waist", waist);

      // make mid-spine point
      const neck = this.get("Neck");
      if (neck) {
        const midSpine = new Vector3();
        midSpine.lerpVectors(waist, neck, 0.6);
        this.set("_spineLower", midSpine);
      }
    }

    return this;
  }

  toString(): string {
    return JSON.stringify({
      actorId: this.actorId,
      positionId: this.positionId,
      namedPoints: this.namedPoints,
    });
  }

  interpolate(a: JointPositions, b: JointPositions, alpha: number) {
    for (const name in a.namedPoints) {
      if (!this.namedPoints[name]) {
        this.jointCnt++;
        this.namedPoints[name] = new Vector3();
      }
      this.namedPoints[name].lerpVectors(
        a.namedPoints[name],
        b.namedPoints[name],
        alpha
      );
    }
  }

  static fromString(json: string): JointPositions | undefined {
    const data = JSON.parse(json);
    const actorId = data.actorId;
    const positionId = data.positionId;
    const namedPoints = data.namedPoints;

    if (actorId === undefined || namedPoints === undefined) {
      return undefined;
    }

    const joints = new JointPositions(positionId, actorId);
    joints.namedPoints = namedPoints;
    return joints;
  }

  static fromSkeletalPosition(
    position: SkeletalPosition,
    actorId?: number
  ): JointPositions {
    const joints = new JointPositions(position.positionId, actorId);
    joints.updateFromSkeletalPosition(position);

    return joints;
  }
}
